import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  DollarSign, TrendingUp, TrendingDown, Wallet, Plus, Pencil, Trash2, Loader2, X,
  BarChart3, Calendar, ArrowUpCircle, ArrowDownCircle, Filter,
  AlertTriangle, Settings, Award, RefreshCw, ChevronDown, ChevronUp, CandlestickChart as CandleIcon
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area, ComposedChart, Bar } from 'recharts';
import SmokeTestPanel from '@/components/premium/SmokeTestPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ==================== TABS ==================== */
const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
  { id: 'pricing', label: 'Precios y Comisiones', icon: Settings },
  { id: 'wallet', label: 'Saldo Proveedor', icon: Wallet },
  { id: 'history', label: 'Historial', icon: Calendar },
];

/* ==================== MAIN COMPONENT ==================== */
const AdminAdministracionPage = () => {
  const { getAuthHeaders } = useAuth();
  const [tab, setTab] = useState('dashboard');
  // Iter 67 · default "annual" para evitar "sin datos" al entrar.
  const [period, setPeriod] = useState('annual');
  const [customDates, setCustomDates] = useState({ start: '', end: '' });
  const [financials, setFinancials] = useState(null);
  const [timeseries, setTimeseries] = useState(null);
  const [pricing, setPricing] = useState([]);
  const [providerWallet, setProviderWallet] = useState({ balance: 0, entries: [] });
  const [loading, setLoading] = useState(true);
  const [chartType, setChartType] = useState('line');
  const [granularity, setGranularity] = useState('day');

  const h = useCallback(() => ({ headers: getAuthHeaders() }), [getAuthHeaders]);

  const [accountingSummary, setAccountingSummary] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const params = period === 'custom' && customDates.start && customDates.end
        ? `?period=custom&start_date=${customDates.start}&end_date=${customDates.end}`
        : `?period=${period}`;

      const [fin, ts, pr, pw, acc] = await Promise.all([
        axios.get(`${API}/admin/financials${params}`, h()),
        axios.get(`${API}/admin/financials/timeseries${params}&granularity=${granularity}`, h()),
        axios.get(`${API}/admin/pricing`, h()),
        axios.get(`${API}/admin/provider-wallet`, h()),
        axios.get(`${API}/accounting/summary${params}`, h()),
      ]);
      setFinancials(fin.data);
      setTimeseries(ts.data);
      setPricing(pr.data);
      setProviderWallet(pw.data);
      setAccountingSummary(acc.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, [period, granularity]);

  const applyCustomFilter = () => {
    if (customDates.start && customDates.end) loadAll();
  };

  if (loading && !financials) return <div className="flex items-center justify-center h-64"><div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-administracion-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Administracion</h1>
          <p className="text-slate-500">Centro de control financiero</p>
        </div>
        <Button onClick={loadAll} variant="outline" size="sm" data-testid="refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Actualizar
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-xl overflow-x-auto">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${tab === t.id ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid={`tab-${t.id}`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* Iter 68 · Smoke test one-click (post-incident confidence) */}
      <SmokeTestPanel getAuthHeaders={getAuthHeaders} />

      {/* Period Filter */}
      <div className="flex items-center gap-2 flex-wrap">
        {['daily', 'weekly', 'monthly', 'annual', 'custom'].map(p => (
          <Button key={p} size="sm" variant={period === p ? 'default' : 'outline'}
            onClick={() => setPeriod(p)} className={period === p ? 'bg-blue-700' : ''}
            data-testid={`period-${p}`}
          >
            {{ daily: 'Diario', weekly: 'Semanal', monthly: 'Mensual', annual: 'Anual', custom: 'Personalizado' }[p]}
          </Button>
        ))}
        {period === 'custom' && (
          <div className="flex items-center gap-2">
            <Input type="date" value={customDates.start} onChange={e => setCustomDates({ ...customDates, start: e.target.value })} className="h-8 w-36 text-xs" data-testid="custom-start" />
            <span className="text-slate-400">→</span>
            <Input type="date" value={customDates.end} onChange={e => setCustomDates({ ...customDates, end: e.target.value })} className="h-8 w-36 text-xs" data-testid="custom-end" />
            <Button size="sm" onClick={applyCustomFilter} className="bg-blue-700 h-8 text-xs" data-testid="apply-custom">Aplicar</Button>
          </div>
        )}
      </div>

      {tab === 'dashboard' && <DashboardTab financials={financials} timeseries={timeseries} chartType={chartType} setChartType={setChartType} granularity={granularity} setGranularity={setGranularity} providerBalance={providerWallet.balance} />}
      {tab === 'pricing' && <PricingTab pricing={pricing} h={h} reload={loadAll} accountingSummary={accountingSummary} />}
      {tab === 'wallet' && <WalletTab wallet={providerWallet} h={h} reload={loadAll} />}
      {tab === 'history' && <HistoryTab h={h} period={period} customDates={customDates} reload={loadAll} />}
    </div>
  );
};

/* ==================== DASHBOARD TAB ==================== */
const DashboardTab = ({ financials, timeseries, chartType, setChartType, granularity, setGranularity, providerBalance }) => {
  const f = financials || {};

  const kpis = [
    { label: 'Total Vendido', desc: 'Ventas de gestores', value: f.total_vendido, icon: DollarSign, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-l-blue-500' },
    { label: 'Pagos Recibidos', desc: 'Cobrado a gestores', value: f.pagos_recibidos, icon: ArrowDownCircle, color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-l-purple-500' },
    { label: 'Comisiones CEO', desc: 'Se restan de ganancia', value: f.comisiones_ceo, icon: Award, color: 'text-amber-500', bg: 'bg-amber-50', border: 'border-l-amber-500', negative: true },
    { label: 'Ganancias Registradas', desc: 'Registradas en contabilidad', value: f.ganancias_manuales, icon: TrendingUp, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-l-emerald-500' },
    { label: 'Costos Proveedor', desc: 'Segun guias de precios', value: f.costos_proveedor, icon: TrendingDown, color: 'text-red-500', bg: 'bg-red-50', border: 'border-l-red-500' },
    { label: 'Saldo Proveedor', desc: 'Balance con proveedor', value: providerBalance, icon: Wallet, color: providerBalance >= 0 ? 'text-indigo-600' : 'text-red-600', bg: providerBalance >= 0 ? 'bg-indigo-50' : 'bg-red-50', border: providerBalance >= 0 ? 'border-l-indigo-500' : 'border-l-red-500', showSign: true },
  ];

  const lineData = timeseries?.line || [];
  const candleData = timeseries?.candle || [];

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {kpis.map((k, i) => (
          <Card key={k.label} className={`p-4 border-0 shadow-sm border-l-4 ${k.border}`} data-testid={`kpi-${i}`}>
            <div className={`w-8 h-8 ${k.bg} rounded-lg flex items-center justify-center mb-2`}>
              <k.icon className={`w-4 h-4 ${k.color}`} />
            </div>
            <p className="text-xs text-slate-500">{k.label}</p>
            <p className={`text-lg font-bold ${k.color}`}>
              {k.negative && (k.value || 0) > 0 ? '-' : ''}{k.showSign && (k.value || 0) < 0 ? '-' : ''}${Math.abs(k.value || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </p>
            <p className="text-[10px] text-slate-400 mt-0.5">{k.desc}</p>
          </Card>
        ))}
      </div>

      {/* Summary row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-3 border-0 shadow-sm text-center">
          <p className="text-xs text-slate-500">Margen</p>
          <p className="text-xl font-bold text-blue-700">{f.avg_margin || 0}%</p>
        </Card>
        <Card className="p-3 border-0 shadow-sm text-center">
          <p className="text-xs text-slate-500">Transacciones</p>
          <p className="text-xl font-bold">{f.total_transactions || 0}</p>
        </Card>
        <Card className="p-3 border-0 shadow-sm text-center">
          <p className="text-xs text-slate-500">Pagos c/Comision</p>
          <p className="text-xl font-bold text-amber-600">{f.payments_with_commission || 0}</p>
        </Card>
        <Card className="p-3 border-0 shadow-sm text-center">
          <p className="text-xs text-slate-500">Crecimiento</p>
          <p className={`text-xl font-bold ${(f.growth_pct || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {(f.growth_pct || 0) >= 0 ? '+' : ''}{f.growth_pct || 0}%
          </p>
        </Card>
      </div>

      {/* Alerts */}
      {f.sales_without_cost > 0 && (
        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700" data-testid="alert-no-cost">
          <AlertTriangle className="w-4 h-4" />
          <span>{f.sales_without_cost} ventas sin costo de proveedor configurado. Agrega precios en la pestana "Precios y Comisiones".</span>
        </div>
      )}

      {/* Top Services */}
      {f.top_services?.length > 0 && (
        <Card className="p-4 border-0 shadow-sm">
          <h3 className="font-semibold text-sm text-slate-700 mb-3">Top Servicios por Ganancia</h3>
          <div className="space-y-2">
            {f.top_services.map((s, i) => (
              <div key={s.name ?? `svc-${i}`} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                <span className="font-medium">{i + 1}. {s.name}</span>
                <div className="flex gap-4 text-xs">
                  <span className="text-red-500">Costo: ${s.cost}</span>
                  <span className="text-blue-600">Venta: ${s.sell}</span>
                  <span className="text-green-600 font-bold">Ganancia: ${s.profit}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Charts - TradingView Style */}
      <Card className="p-0 border-0 shadow-sm overflow-hidden rounded-xl">
        <div className="flex items-center justify-between px-5 py-3 bg-[#131722] border-b border-[#2a2e39]">
          <h3 className="font-semibold text-slate-200 text-sm">Tendencia Financiera</h3>
          <div className="flex gap-2">
            <div className="flex bg-[#2a2e39] rounded p-0.5">
              <button onClick={() => setChartType('line')} className={`px-3 py-1 text-xs rounded ${chartType === 'line' ? 'bg-[#2962ff] text-white' : 'text-slate-400 hover:text-slate-200'}`} data-testid="chart-line">Lineas</button>
              <button onClick={() => setChartType('candle')} className={`px-3 py-1 text-xs rounded ${chartType === 'candle' ? 'bg-[#2962ff] text-white' : 'text-slate-400 hover:text-slate-200'}`} data-testid="chart-candle">Velas</button>
            </div>
            <select value={granularity} onChange={e => setGranularity(e.target.value)} className="h-7 text-xs border-0 rounded px-2 bg-[#2a2e39] text-slate-300" data-testid="granularity-select">
              <option value="day">1D</option>
              <option value="week">1W</option>
              <option value="month">1M</option>
            </select>
          </div>
        </div>

        <div className="bg-[#131722] px-2 pb-3">
          {lineData.length === 0 ? (
            <div className="flex items-center justify-center h-48 text-slate-500 text-sm">Sin datos para el periodo seleccionado</div>
          ) : chartType === 'line' ? (
            <ResponsiveContainer width="100%" height={360}>
              <ComposedChart data={lineData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2e39" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#787b86' }} axisLine={{ stroke: '#2a2e39' }} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: '#787b86' }} axisLine={{ stroke: '#2a2e39' }} tickLine={false} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                <Tooltip
                  formatter={(v, name) => [`$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2 })}`, name]}
                  contentStyle={{ background: '#1e222d', border: '1px solid #2a2e39', borderRadius: '8px', color: '#d1d4dc', fontSize: '12px' }}
                  labelStyle={{ color: '#787b86', marginBottom: '4px' }}
                />
                <Legend wrapperStyle={{ fontSize: '11px', color: '#787b86' }} />
                <Area type="monotone" dataKey="pagos" name="Pagos Recibidos" fill="rgba(41,98,255,0.1)" stroke="#2962ff" strokeWidth={2} />
                <Area type="monotone" dataKey="costos" name="Costos Proveedor" fill="rgba(234,57,67,0.08)" stroke="#ea3943" strokeWidth={1.5} strokeDasharray="4 2" />
                <Line type="monotone" dataKey="ganancias" name="Ganancias" stroke="#16c784" strokeWidth={2.5} dot={{ r: 3, fill: '#16c784', strokeWidth: 0 }} activeDot={{ r: 5, fill: '#16c784' }} />
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <TradingCandlestickChart data={candleData} />
          )}
        </div>
      </Card>
    </div>
  );
};

/* ==================== TRADING CANDLESTICK CHART ==================== */
const TradingCandlestickChart = ({ data }) => {
  if (!data || data.length === 0) return <div className="h-48 flex items-center justify-center text-slate-500 text-sm">Sin datos de velas</div>;

  const chartData = data.map(d => ({
    date: d.date,
    open: d.open,
    close: d.close,
    high: d.high,
    low: d.low,
    isGreen: d.close >= d.open,
  }));

  return (
    <ResponsiveContainer width="100%" height={360}>
      <ComposedChart data={chartData} barGap={0} barCategoryGap="20%">
        <CartesianGrid strokeDasharray="3 3" stroke="#2a2e39" />
        <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#787b86' }} axisLine={{ stroke: '#2a2e39' }} tickLine={false} />
        <YAxis tick={{ fontSize: 10, fill: '#787b86' }} axisLine={{ stroke: '#2a2e39' }} tickLine={false} tickFormatter={v => `$${v.toFixed(0)}`} />
        <Tooltip
          content={({ active, payload }) => {
            if (!active || !payload?.length) return null;
            const d = payload[0]?.payload;
            const isUp = d.close >= d.open;
            return (
              <div className="p-3 rounded-lg border text-xs" style={{ background: '#1e222d', borderColor: '#2a2e39' }}>
                <p className="font-semibold text-slate-300 mb-1.5">{d.date}</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  <span className="text-slate-500">Apertura:</span><span className="text-slate-200 font-mono">${d.open?.toFixed(2)}</span>
                  <span className="text-slate-500">Cierre:</span><span className={`font-mono font-bold ${isUp ? 'text-[#16c784]' : 'text-[#ea3943]'}`}>${d.close?.toFixed(2)}</span>
                  <span className="text-slate-500">Maximo:</span><span className="text-[#16c784] font-mono">${d.high?.toFixed(2)}</span>
                  <span className="text-slate-500">Minimo:</span><span className="text-[#ea3943] font-mono">${d.low?.toFixed(2)}</span>
                </div>
              </div>
            );
          }}
        />
        <Bar dataKey="high" fill="transparent" />
        <Line type="monotone" dataKey="high" stroke="#16c784" strokeWidth={1} dot={{ r: 2, fill: '#16c784' }} name="Maximo" />
        <Line type="monotone" dataKey="low" stroke="#ea3943" strokeWidth={1} dot={{ r: 2, fill: '#ea3943' }} name="Minimo" />
        <Line type="monotone" dataKey="open" stroke="#2962ff" strokeWidth={2} dot={{ r: 3, fill: '#2962ff' }} name="Apertura" />
        <Line type="monotone" dataKey="close" stroke="#e8b730" strokeWidth={2} dot={{ r: 3, fill: '#e8b730' }} name="Cierre" />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

/* ==================== PRICING TAB ==================== */
const PricingTab = ({ pricing, h, reload, accountingSummary }) => {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [form, setForm] = useState({
    service_name: '', description: '', provider_cost: '', selling_price: '', commission_pct: '2.5', category: 'vuelos'
  });

  const acc = accountingSummary || {};

  const resetForm = () => { setForm({ service_name: '', description: '', provider_cost: '', selling_price: '', commission_pct: '2.5', category: 'vuelos' }); setEditing(null); setShowForm(false); };

  const startEdit = (p) => {
    setForm({ service_name: p.service_name, description: p.description || '', provider_cost: p.provider_cost?.toString(), selling_price: p.selling_price?.toString(), commission_pct: p.commission_pct?.toString() || '2.5', category: p.category || 'vuelos' });
    setEditing(p.id);
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, provider_cost: parseFloat(form.provider_cost), selling_price: parseFloat(form.selling_price) || 0, commission_pct: parseFloat(form.commission_pct) };
    try {
      if (editing) {
        await axios.put(`${API}/admin/pricing/${editing}`, payload, h());
      } else {
        await axios.post(`${API}/admin/pricing`, payload, h());
      }
      toast.success(editing ? 'Actualizado' : 'Creado');
      resetForm();
      reload();
    } catch (e) { toast.error('Error'); } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar este precio?')) return;
    try { await axios.delete(`${API}/admin/pricing/${id}`, h()); reload(); } catch (e) { toast.error('Error'); }
  };

  const handleRecalculate = async () => {
    if (!window.confirm('Esto aplicara los precios configurados a todas las ventas que no tienen costo de proveedor. ¿Continuar?')) return;
    setRecalculating(true);
    try {
      const res = await axios.post(`${API}/ventas/recalculate-costs`, {}, h());
      toast.success(res.data.message);
      reload();
    } catch (e) { toast.error('Error al recalcular'); }
    finally { setRecalculating(false); }
  };

  const cost = parseFloat(form.provider_cost) || 0;
  const sell = parseFloat(form.selling_price) || 0;
  const commPct = parseFloat(form.commission_pct) || 0;
  const commAmount = sell * commPct / 100;
  const profit = sell - cost - commAmount;

  // Calculate totals from pricing table
  const totalProviderCost = pricing.reduce((s, p) => s + (p.provider_cost || 0), 0);
  const totalSellingPrice = pricing.reduce((s, p) => s + (p.selling_price || 0), 0);
  const totalProfitPricing = pricing.reduce((s, p) => s + (p.profit_per_unit || 0), 0);

  return (
    <div className="space-y-5">
      {/* Financial Summary KPIs - clearly differentiated */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="pricing-kpis">
        <Card className="p-4 border-0 shadow-sm border-l-4 border-l-blue-500" data-testid="kpi-pagos-gestores">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center shrink-0">
              <ArrowDownCircle className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Pagos Recibidos de Gestores</p>
              <p className="text-2xl font-bold text-blue-700">${(acc.payments_from_gestores || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
              <p className="text-xs text-slate-400 mt-1">Dinero cobrado a los gestores por servicios vendidos</p>
            </div>
          </div>
        </Card>

        <Card className="p-4 border-0 shadow-sm border-l-4 border-l-indigo-500" data-testid="kpi-ventas-totales">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center shrink-0">
              <TrendingUp className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Ventas Totales</p>
              <p className="text-2xl font-bold text-indigo-700">${(acc.total_sales || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
              <p className="text-xs text-slate-400 mt-1">Monto total de boletos y servicios vendidos</p>
            </div>
          </div>
        </Card>

        <Card className="p-4 border-0 shadow-sm border-l-4 border-l-emerald-500" data-testid="kpi-ganancias-registradas">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center shrink-0">
              <DollarSign className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Ganancias Registradas</p>
              <p className="text-2xl font-bold text-emerald-700">${(acc.manual_profits || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
              <p className="text-xs text-slate-400 mt-1">Ganancias ingresadas manualmente en contabilidad</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Comisiones additional info */}
      {(acc.total_commissions || 0) > 0 && (
        <Card className="p-3 border-0 shadow-sm bg-amber-50/50 flex items-center gap-3" data-testid="comisiones-info">
          <Award className="w-5 h-5 text-amber-600 shrink-0" />
          <div className="text-sm">
            <span className="text-slate-600">Comisiones CEO pagadas: </span>
            <span className="font-bold text-amber-700">${(acc.total_commissions || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
            <span className="text-slate-400 ml-2 text-xs">(Descontadas de los pagos de gestores, opcional por pago)</span>
          </div>
        </Card>
      )}

      {/* Separator */}
      <div className="border-t border-slate-200 pt-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-slate-800">Precios de Proveedor por Servicio</h2>
            <p className="text-xs text-slate-400 mt-0.5">Configura el costo base de cada servicio para auto-calcular ganancias al crear ventas</p>
          </div>
          <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-blue-700" data-testid="add-pricing-btn">
            <Plus className="w-4 h-4 mr-1" /> Agregar Precio
          </Button>
        </div>
      </div>

      {showForm && (
        <Card className="p-5 border-blue-200 bg-blue-50" data-testid="pricing-form">
          <div className="flex justify-between mb-3">
            <h3 className="font-semibold">{editing ? 'Editar' : 'Nuevo'} Precio de Proveedor</h3>
            <button onClick={resetForm}><X className="w-5 h-5 text-slate-400" /></button>
          </div>
          <form onSubmit={handleSave} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-1">
              <Label className="text-xs">Servicio / Ruta</Label>
              <Input value={form.service_name} onChange={e => setForm({ ...form, service_name: e.target.value })} placeholder="Ej: HAV-MIA, Vuelo Nacional" required data-testid="pricing-name" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Descripcion</Label>
              <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Detalles opcionales" data-testid="pricing-desc" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Costo Proveedor ($)</Label>
              <Input type="number" step="0.01" value={form.provider_cost} onChange={e => setForm({ ...form, provider_cost: e.target.value })} required data-testid="pricing-cost" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Precio Venta ($)</Label>
              <Input type="number" step="0.01" value={form.selling_price} onChange={e => setForm({ ...form, selling_price: e.target.value })} data-testid="pricing-sell" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Comision CEO (%)</Label>
              <Input type="number" step="any" min="0" max="100" value={form.commission_pct} onChange={e => setForm({ ...form, commission_pct: e.target.value })} placeholder="Ej: 2.5" className="w-full" data-testid="pricing-comm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Categoria</Label>
              <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm" data-testid="pricing-cat">
                <option value="vuelos">Vuelos</option>
                <option value="paquetes">Paquetes</option>
                <option value="servicios">Servicios</option>
                <option value="otros">Otros</option>
              </select>
            </div>
            {sell > 0 && cost > 0 && (
              <div className="sm:col-span-2 lg:col-span-3 p-3 bg-white rounded-lg border border-green-200">
                <p className="text-xs font-medium text-slate-500 mb-2">Vista previa del calculo automatico:</p>
                <div className="grid grid-cols-4 gap-3 text-sm text-center">
                  <div><p className="text-slate-500 text-xs">Costo Proveedor</p><p className="font-bold text-red-600">${cost.toFixed(2)}</p></div>
                  <div><p className="text-slate-500 text-xs">Comision CEO ({commPct}%)</p><p className="font-bold text-amber-600">${commAmount.toFixed(2)}</p></div>
                  <div><p className="text-slate-500 text-xs">Precio Venta</p><p className="font-bold text-blue-600">${sell.toFixed(2)}</p></div>
                  <div><p className="text-slate-500 text-xs">Ganancia Neta</p><p className={`font-bold ${profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>${profit.toFixed(2)}</p></div>
                </div>
              </div>
            )}
            <div className="sm:col-span-2 lg:col-span-3">
              <Button type="submit" disabled={saving} className="bg-blue-700" data-testid="pricing-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? 'Actualizar' : 'Guardar')}</Button>
            </div>
          </form>
        </Card>
      )}

      {pricing.length === 0 ? (
        <Card className="p-10 text-center border-0 shadow-sm" data-testid="pricing-empty">
          <Settings className="w-10 h-10 mx-auto text-slate-300 mb-2" />
          <p className="text-slate-500 font-medium">Sin precios de proveedor configurados</p>
          <p className="text-xs text-slate-400 mt-1">Agrega el costo base del proveedor para cada servicio/ruta para que las ventas auto-calculen la ganancia</p>
        </Card>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="pricing-table">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Servicio</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Descripcion</th>
                  <th className="text-right px-4 py-3 font-medium text-slate-600">Costo Prov.</th>
                  <th className="text-right px-4 py-3 font-medium text-slate-600">Comision %</th>
                  <th className="text-right px-4 py-3 font-medium text-slate-600">Precio Venta</th>
                  <th className="text-right px-4 py-3 font-medium text-slate-600">Ganancia</th>
                  <th className="text-right px-4 py-3 font-medium text-slate-600">Margen</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {pricing.map(p => (
                  <tr key={p.id} className="border-b hover:bg-slate-50/50" data-testid={`pricing-row-${p.id}`}>
                    <td className="px-4 py-3 font-medium">{p.service_name}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs max-w-xs truncate">{p.description || '-'}</td>
                    <td className="px-4 py-3 text-right text-red-600 font-medium">${p.provider_cost?.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right text-amber-600">{p.commission_pct}%</td>
                    <td className="px-4 py-3 text-right text-blue-600 font-medium">${p.selling_price?.toFixed(2)}</td>
                    <td className={`px-4 py-3 text-right font-bold ${(p.profit_per_unit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>${(p.profit_per_unit || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-right">{(p.margin_pct || 0).toFixed(1)}%</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => startEdit(p)} className="p-1 text-blue-500" data-testid={`edit-pricing-${p.id}`}><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => handleDelete(p.id)} className="p-1 text-red-500" data-testid={`delete-pricing-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Totals summary for pricing table */}
          <Card className="p-3 border-0 shadow-sm bg-slate-50">
            <div className="grid grid-cols-3 gap-4 text-sm text-center">
              <div>
                <p className="text-xs text-slate-500">Total Costos Proveedor</p>
                <p className="font-bold text-red-600">${totalProviderCost.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Total Precios Venta</p>
                <p className="font-bold text-blue-600">${totalSellingPrice.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Total Ganancia por Servicio</p>
                <p className={`font-bold ${totalProfitPricing >= 0 ? 'text-green-600' : 'text-red-600'}`}>${totalProfitPricing.toFixed(2)}</p>
              </div>
            </div>
          </Card>
        </>
      )}

      {/* Info box about how auto-calculation works */}
      <Card className="p-4 border-0 shadow-sm bg-blue-50/50" data-testid="pricing-info-box">
        <div className="flex gap-3">
          <AlertTriangle className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
          <div className="text-sm text-slate-600 space-y-2">
            <p className="font-medium text-blue-800">Como funciona el auto-calculo de ganancias</p>
            <p>Al crear una nueva venta, el sistema busca automaticamente el costo del proveedor configurado aqui y calcula:</p>
            <p className="font-mono text-xs bg-white px-2 py-1 rounded border inline-block">Ganancia Neta = Precio Venta - Costo Proveedor - Comision CEO</p>
            <p className="text-xs text-slate-400">Las comisiones CEO se <strong>restan</strong> de la ganancia bruta. No afectan el saldo del gestor.</p>
            <p className="text-xs text-slate-400">Si no hay precio configurado para el servicio, la venta se registra sin costo de proveedor (aparecera como alerta en el Dashboard).</p>
            <div className="pt-2 border-t border-blue-200">
              <Button onClick={handleRecalculate} disabled={recalculating} variant="outline" size="sm" className="text-xs" data-testid="recalculate-btn">
                {recalculating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                Recalcular ventas historicas sin costo (opcional)
              </Button>
              <span className="text-xs text-slate-400 ml-2">Aplica los precios configurados a ventas antiguas que no tenian costo de proveedor</span>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

/* ==================== PROVIDER WALLET TAB ==================== */
const WalletTab = ({ wallet, h, reload }) => {
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ amount: '', description: '', entry_type: 'recarga' });
  const [saving, setSaving] = useState(false);

  const resetForm = () => { setForm({ amount: '', description: '', entry_type: 'recarga' }); setEditing(null); setShowAdd(false); };

  const startEdit = (entry) => {
    setForm({
      amount: Math.abs(entry.amount).toString(),
      description: entry.description || '',
      entry_type: entry.entry_type || (entry.amount >= 0 ? 'recarga' : 'deduccion'),
    });
    setEditing(entry.id);
    setShowAdd(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const amt = parseFloat(form.amount);
    const payload = {
      amount: form.entry_type === 'deduccion' ? -Math.abs(amt) : Math.abs(amt),
      description: form.description,
      entry_type: form.entry_type,
    };
    try {
      if (editing) {
        await axios.put(`${API}/admin/provider-wallet/${editing}`, payload, h());
        toast.success('Movimiento actualizado');
      } else {
        await axios.post(`${API}/admin/provider-wallet`, payload, h());
        toast.success('Movimiento registrado');
      }
      resetForm();
      reload();
    } catch (e) { toast.error('Error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este movimiento? Se recalcularan los saldos.')) return;
    try {
      await axios.delete(`${API}/admin/provider-wallet/${id}`, h());
      toast.success('Movimiento eliminado');
      reload();
    } catch (e) { toast.error('Error al eliminar'); }
  };

  return (
    <div className="space-y-4">
      <Card className="p-6 bg-gradient-to-br from-indigo-600 to-indigo-800 text-white border-0">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2"><Wallet className="w-6 h-6" /><span className="text-indigo-200">Saldo con Proveedor</span></div>
            <p className="text-3xl font-bold" data-testid="provider-balance">${(wallet.balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
          </div>
          <Button onClick={() => { resetForm(); setShowAdd(!showAdd); }} variant="outline" className="text-white border-white/30 hover:bg-white/10" data-testid="add-wallet-entry">
            <Plus className="w-4 h-4 mr-1" /> Movimiento
          </Button>
        </div>
        {wallet.balance < 100 && wallet.balance !== 0 && (
          <div className="mt-3 flex items-center gap-2 p-2 bg-red-500/20 rounded-lg text-sm">
            <AlertTriangle className="w-4 h-4" /> Saldo bajo. Considera recargar con tu proveedor.
          </div>
        )}
      </Card>

      {showAdd && (
        <Card className="p-5 border-indigo-200 bg-indigo-50" data-testid="wallet-entry-form">
          <div className="flex justify-between mb-3">
            <h3 className="font-semibold text-sm">{editing ? 'Editar Movimiento' : 'Nuevo Movimiento'}</h3>
            <button onClick={resetForm}><X className="w-5 h-5 text-slate-400" /></button>
          </div>
          <form onSubmit={handleSave} className="grid sm:grid-cols-3 gap-4">
            <div className="space-y-1">
              <Label className="text-xs">Tipo</Label>
              <select value={form.entry_type} onChange={e => setForm({ ...form, entry_type: e.target.value })} className="w-full h-10 px-3 rounded-md border text-sm" data-testid="entry-type">
                <option value="recarga">Recarga</option>
                <option value="deduccion">Deduccion</option>
                <option value="ajuste">Ajuste</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Monto ($)</Label>
              <Input type="number" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} required data-testid="entry-amount" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Descripcion</Label>
              <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Detalle del movimiento" data-testid="entry-desc" />
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={saving} className="bg-indigo-700" data-testid="entry-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? 'Actualizar' : 'Registrar')}</Button>
              {editing && <Button type="button" variant="outline" onClick={resetForm}>Cancelar</Button>}
            </div>
          </form>
        </Card>
      )}

      {/* History */}
      <Card className="p-4 border-0 shadow-sm">
        <h3 className="font-semibold text-sm text-slate-700 mb-3">Historial de Movimientos</h3>
        {wallet.entries?.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-6">Sin movimientos</p>
        ) : (
          <div className="space-y-2">
            {wallet.entries.map(e => (
              <div key={e.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`wallet-entry-${e.id}`}>
                <div className="flex items-center gap-3">
                  {e.amount > 0 ? <ArrowUpCircle className="w-5 h-5 text-green-500" /> : <ArrowDownCircle className="w-5 h-5 text-red-500" />}
                  <div>
                    <p className="text-sm font-medium">{e.description || e.entry_type}</p>
                    <p className="text-xs text-slate-400">{new Date(e.created_at).toLocaleString('es-ES')}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className={`font-bold ${e.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {e.amount > 0 ? '+' : ''}${Math.abs(e.amount).toFixed(2)}
                    </p>
                    <p className="text-xs text-slate-400">Saldo: ${e.balance_after?.toFixed(2)}</p>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => startEdit(e)} className="p-1.5 text-blue-500 hover:bg-blue-50 rounded" data-testid={`edit-entry-${e.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => handleDelete(e.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded" data-testid={`delete-entry-${e.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

/* ==================== HISTORY TAB ==================== */
const HistoryTab = ({ h, period, customDates, reload }) => {
  const [sales, setSales] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('sales');
  const [editingSale, setEditingSale] = useState(null);
  const [editingPayment, setEditingPayment] = useState(null);

  const buildParams = () => {
    if (period === 'custom' && customDates.start && customDates.end)
      return `?start_date=${customDates.start}&end_date=${customDates.end}`;
    if (period === 'weekly' || period === 'monthly' || period === 'annual')
      return `?period=${period}`;
    return '';
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = buildParams();
      const [s, p] = await Promise.all([
        axios.get(`${API}/ventas${params}`, h()),
        axios.get(`${API}/payments${params}`, h()),
      ]);
      setSales(s.data.slice().sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      setPayments(p.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [period, customDates]);

  const handleDeleteSale = async (id) => {
    if (!window.confirm('¿Eliminar esta venta? Se reversa la wallet del gestor y el saldo del proveedor.')) return;
    try {
      await axios.delete(`${API}/ventas/${id}`, h());
      toast.success('Venta eliminada');
      load();
      reload();
    } catch (e) { toast.error('Error al eliminar'); }
  };

  const handleDeletePayment = async (id) => {
    if (!window.confirm('¿Eliminar este pago? Se reversa la wallet del gestor.')) return;
    try {
      await axios.delete(`${API}/payments/${id}`, h());
      toast.success('Pago eliminado');
      load();
      reload();
    } catch (e) { toast.error('Error al eliminar'); }
  };

  if (loading) return <div className="flex items-center justify-center h-32"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;

  const totalSalesAmount = sales.reduce((s, v) => s + v.amount, 0);
  const totalCosts = sales.reduce((s, v) => s + (v.provider_cost || 0), 0);
  const totalProfit = sales.reduce((s, v) => s + (v.profit || 0), 0);
  const totalCommSales = sales.reduce((s, v) => s + (v.commission_amount || 0), 0);
  const totalPaymentsAmount = payments.reduce((s, p) => s + p.amount, 0);
  const totalPaymentsComm = payments.reduce((s, p) => s + (p.commission || 0), 0);
  const totalPaymentsNeto = totalPaymentsAmount - totalPaymentsComm;

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Button size="sm" variant={view === 'sales' ? 'default' : 'outline'} onClick={() => setView('sales')} className={view === 'sales' ? 'bg-blue-700' : ''} data-testid="view-sales">Ventas ({sales.length})</Button>
        <Button size="sm" variant={view === 'payments' ? 'default' : 'outline'} onClick={() => setView('payments')} className={view === 'payments' ? 'bg-blue-700' : ''} data-testid="view-payments">Pagos ({payments.length})</Button>
      </div>

      {view === 'sales' && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="sales-history-table">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-3 py-2 text-xs font-medium text-slate-600">Fecha</th>
                <th className="text-left px-3 py-2 text-xs font-medium text-slate-600">Ruta</th>
                <th className="text-left px-3 py-2 text-xs font-medium text-slate-600">Pasajero</th>
                <th className="text-left px-3 py-2 text-xs font-medium text-slate-600">Gestor</th>
                <th className="text-right px-3 py-2 text-xs font-medium text-slate-600">Ingreso</th>
                <th className="text-right px-3 py-2 text-xs font-medium text-slate-600">Costo Prov.</th>
                <th className="text-right px-3 py-2 text-xs font-medium text-slate-600">Comision CEO</th>
                <th className="text-right px-3 py-2 text-xs font-medium text-slate-600">Ganancia</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {sales.map(s => {
                const prof = s.profit != null ? s.profit : (s.provider_cost ? s.amount - s.provider_cost : null);
                return (
                  <tr key={s.id} className="border-b hover:bg-slate-50/50">
                    <td className="px-3 py-2 text-xs text-slate-500">{new Date(s.created_at).toLocaleDateString('es-ES')}</td>
                    <td className="px-3 py-2 font-medium">{s.route_origin}→{s.route_destination}</td>
                    <td className="px-3 py-2 text-slate-600 text-xs">{s.passenger_name}</td>
                    <td className="px-3 py-2 text-slate-500 text-xs">{s.gestor_name || '-'}</td>
                    <td className="px-3 py-2 text-right font-medium text-blue-600">${s.amount?.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right text-red-500">{s.provider_cost ? `$${s.provider_cost.toFixed(2)}` : <span className="text-slate-300">-</span>}</td>
                    <td className="px-3 py-2 text-right text-amber-600">{s.commission_amount ? `$${s.commission_amount.toFixed(2)}` : <span className="text-slate-300">-</span>}</td>
                    <td className={`px-3 py-2 text-right font-bold ${prof != null ? (prof >= 0 ? 'text-green-600' : 'text-red-600') : 'text-slate-300'}`}>
                      {prof != null ? `$${prof.toFixed(2)}` : '-'}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => handleDeleteSale(s.id)} className="p-1 text-red-400 hover:text-red-600" data-testid={`delete-sale-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="mt-3 p-3 bg-blue-50 rounded-lg grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
            <span>Ingresos: <strong className="text-blue-700">${totalSalesAmount.toFixed(2)}</strong></span>
            <span>Costos: <strong className="text-red-600">${totalCosts.toFixed(2)}</strong></span>
            <span>Comisiones: <strong className="text-amber-600">${totalCommSales.toFixed(2)}</strong></span>
            <span>Ganancia: <strong className="text-green-600">${totalProfit.toFixed(2)}</strong></span>
          </div>
        </div>
      )}

      {view === 'payments' && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="payments-history-table">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-3 py-2 text-xs font-medium text-slate-600">Fecha</th>
                <th className="text-left px-3 py-2 text-xs font-medium text-slate-600">Gestor</th>
                <th className="text-left px-3 py-2 text-xs font-medium text-slate-600">Descripcion</th>
                <th className="text-right px-3 py-2 text-xs font-medium text-slate-600">Monto</th>
                <th className="text-right px-3 py-2 text-xs font-medium text-slate-600">Comision CEO</th>
                <th className="text-right px-3 py-2 text-xs font-medium text-slate-600">Neto</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {payments.map(p => {
                const comm = p.commission || 0;
                return (
                  <tr key={p.id} className="border-b hover:bg-slate-50/50">
                    <td className="px-3 py-2 text-xs text-slate-500">{p.payment_date || '-'}</td>
                    <td className="px-3 py-2 font-medium">{p.gestor_name || '-'}</td>
                    <td className="px-3 py-2 text-slate-500 text-xs max-w-xs truncate">{p.description || '-'}</td>
                    <td className="px-3 py-2 text-right font-medium text-blue-600">${p.amount?.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right text-amber-600">{comm > 0 ? `$${comm.toFixed(2)}` : <span className="text-slate-300">-</span>}</td>
                    <td className="px-3 py-2 text-right font-bold text-green-600">${(p.amount - comm).toFixed(2)}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => handleDeletePayment(p.id)} className="p-1 text-red-400 hover:text-red-600" data-testid={`delete-payment-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="mt-3 p-3 bg-blue-50 rounded-lg grid grid-cols-3 gap-2 text-sm">
            <span>Total Pagos: <strong className="text-blue-700">${totalPaymentsAmount.toFixed(2)}</strong></span>
            <span>Total Comisiones: <strong className="text-amber-600">${totalPaymentsComm.toFixed(2)}</strong></span>
            <span>Total Neto: <strong className="text-green-600">${totalPaymentsNeto.toFixed(2)}</strong></span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAdministracionPage;
